import React, { useContext } from 'react';
import { Headphones, ListMusic, Pause, Play, Radio, SkipBack, SkipForward } from 'lucide-react';
import { AppContext } from '../../Context';
import './EstimulacionNeuronal.css';

const formatTrackName = (name = '') => (
  name
    .replace(/\.[^/.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim() || 'Estimulación neuronal'
);

const EstimulacionNeuronal = ({ compact = false }) => {
  const {
    audios,
    currentAudio,
    currentAudioIndex,
    isPlaying,
    isAudioPlayerActive,
    activateAudioPlayer,
    togglePlayPause,
    playNext,
    playPrev,
    selectAudio,
  } = useContext(AppContext);

  const hasAudios = audios.length > 0;
  const isCurrentPlaying = isAudioPlayerActive && isPlaying;

  const handlePrimaryAction = () => {
    if (!hasAudios) return;

    if (isAudioPlayerActive) {
      togglePlayPause();
      return;
    }

    activateAudioPlayer();
  };

  if (compact) {
    return (
      <div className="neural-compact-player">
        <div className="neural-compact-player__track">
          <Headphones size={18} />
          <span>{hasAudios ? formatTrackName(currentAudio?.name) : 'Cargando pistas...'}</span>
        </div>
        <button
          type="button"
          className="neural-compact-player__button"
          onClick={handlePrimaryAction}
          disabled={!hasAudios}
          aria-label={isCurrentPlaying ? 'Pausar estimulación neuronal' : 'Reproducir estimulación neuronal'}
        >
          {isCurrentPlaying ? <Pause size={18} /> : <Play size={18} />}
          <span>{isCurrentPlaying ? 'Pausar' : 'Reproducir'}</span>
        </button>
      </div>
    );
  }

  if (!hasAudios) {
    return (
      <section className="neural-session neural-session--empty">
        <Headphones size={42} />
        <h1>Estimulación neuronal</h1>
        <p>Cargando pistas...</p>
      </section>
    );
  }

  return (
    <section className="neural-session">
      <header className="neural-session__header">
        <div>
          <span className="neural-session__eyebrow">Modo alumno</span>
          <h1>Estimulación neuronal</h1>
        </div>
        <div className={`neural-session__badge ${isAudioPlayerActive ? 'is-active' : ''}`}>
          <Radio size={18} />
          <span>{isAudioPlayerActive ? 'Activa' : 'Inactiva'}</span>
        </div>
      </header>

      <div className="neural-session__surface">
        <div className="neural-session__now">
          <span className="neural-session__cover" aria-hidden="true">
            <Headphones size={32} />
          </span>
          <div>
            <span>Actual</span>
            <strong>{formatTrackName(currentAudio?.name)}</strong>
          </div>
        </div>

        <div className="neural-session__controls">
          <button type="button" onClick={playPrev} aria-label="Pista anterior" title="Pista anterior">
            <SkipBack size={22} />
          </button>
          <button
            type="button"
            className="neural-session__primary"
            onClick={handlePrimaryAction}
            aria-label={isCurrentPlaying ? 'Pausar estimulación neuronal' : 'Reproducir estimulación neuronal'}
          >
            {isCurrentPlaying ? <Pause size={24} /> : <Play size={24} />}
            <span>{isCurrentPlaying ? 'Pausar' : 'Reproducir'}</span>
          </button>
          <button type="button" onClick={playNext} aria-label="Siguiente pista" title="Siguiente pista">
            <SkipForward size={22} />
          </button>
        </div>
      </div>

      <div className="neural-session__playlist">
        <div className="neural-session__playlist-title">
          <ListMusic size={20} />
          <h2>Pistas disponibles</h2>
        </div>

        <div className="neural-session__tracks">
          {audios.map((audio, index) => {
            const isCurrent = index === currentAudioIndex;

            return (
              <button
                type="button"
                key={`${audio.name}-${index}`}
                className={`neural-session__track ${isCurrent ? 'is-current' : ''}`}
                onClick={() => selectAudio(index, true)}
              >
                <span>{formatTrackName(audio.name)}</span>
                {isCurrent && <strong>{isAudioPlayerActive ? 'Activa' : 'Actual'}</strong>}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default EstimulacionNeuronal;
